import { useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys, createQueryOptions, createMutationOptions } from '../config/queryConfig';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import { useMsal } from '@azure/msal-react';
import API_CONFIG from '../config/apiConfig';

// Helper to prefix API URLs
// Use REACT_APP_API_URL environment variable if set, otherwise use production URL
const API_BASE_URL = API_CONFIG.BASE_URL;
function apiUrl(path) {
    if (/^https?:\/\//.test(path)) return path; // already absolute
    if (!API_BASE_URL) {
        // Ensure path always starts with a single '/'
        return path.startsWith('/') ? path : '/' + path;
    }

    // In dev, server routes are mounted under `/api/*`. If a dev base URL is configured
    // without the `/api` suffix (e.g., http://localhost:3001), normalize it.
    let base = API_BASE_URL.replace(/\/$/, '');
    if (process.env.NODE_ENV === 'development' && !/\/api$/i.test(base)) {
        base = base + '/api';
    }

    return base + (path.startsWith('/') ? path : '/' + path);
}

// API request helper using MSAL authentication
const apiFetch = async (authenticatedFetch, endpoint, options = {}) => {
    const fullUrl = apiUrl(endpoint);
    
    // Handle FormData - don't set Content-Type, let browser set it with boundary
    if (options.body instanceof FormData) {
        const { headers, ...restOptions } = options;
        options = restOptions; // Remove headers to let browser set them for FormData
    }
    
    const response = await authenticatedFetch(fullUrl, options);
    if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        let errorDetails = null;

        // Read the body once, then try to parse it as JSON. Reading it twice
        // (e.g. response.json() then response.text()) throws
        // "body stream already read" and masks the real server error.
        const rawBody = await response.text().catch(() => '');
        if (rawBody) {
            try {
                errorDetails = JSON.parse(rawBody);
                errorMessage = errorDetails.message || errorDetails.error || errorMessage;
            } catch (e) {
                // Body wasn't JSON; fall back to the raw text.
                errorMessage = rawBody;
            }
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.details = errorDetails;
        throw error;
    }
    return response.json();
};

/**
 * Custom hook that wraps useQuery with centralized configuration
 * @param {Array|string} queryKey - The query key
 * @param {Function} queryFn - The query function
 * @param {Object} options - Additional options
 * @returns {Object} useQuery result
 */
export function useConfiguredQuery(queryKey, queryFn, options = {}) {
    const queryOptions = createQueryOptions(queryKey, queryFn, options);
    return useQuery(queryOptions);
}

/**
 * Custom hook that wraps useMutation with centralized configuration
 * @param {Function} mutationFn - The mutation function
 * @param {Object} options - Additional options
 * @returns {Object} useMutation result
 */
export function useConfiguredMutation(mutationFn, options = {}) {
    const mutationOptions = createMutationOptions(mutationFn, options);
    return useMutation(mutationOptions);
}

// Current user query
export function useCurrentUser(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.currentUser,
        async () => {
            return apiFetch(authenticatedFetch, '/users/me');
        },
        options
    );
}

// User settings query
export function useUserSettings(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.userSettings,
        async () => {
            return apiFetch(authenticatedFetch, '/users/me/settings');
        },
        options
    );
}

// Update user settings mutation
export function useUpdateUserSettings() {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (settings) => {
            return apiFetch(authenticatedFetch, '/users/me/settings', {
                method: 'PATCH',
                body: JSON.stringify(settings)
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.userSettings);
            }
        }
    );
}

// Reports - Stale items (no activity in last N days)
export function useStaleItemsReport({ days = 10, projectId = null, openOnly = false } = {}, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    const normalizedDays = Number.isFinite(Number(days)) ? Number.parseInt(days, 10) : 10;
    const normalizedProjectId = projectId != null ? String(projectId) : null;
    const normalizedOpenOnly = !!openOnly;

    const params = {
        days: normalizedDays,
        projectId: normalizedProjectId,
        openOnly: normalizedOpenOnly,
    };

    return useConfiguredQuery(
        queryKeys.staleItemsReport(params),
        async () => {
            const search = new URLSearchParams();
            search.set('days', String(normalizedDays));
            if (normalizedProjectId != null) search.set('project_id', String(normalizedProjectId));
            if (normalizedOpenOnly) search.set('open_only', 'true');

            return apiFetch(authenticatedFetch, `/reports/stale-items?${search.toString()}`);
        },
        {
            staleTime: 1000 * 60 * 2,
            ...options,
        }
    );
}

// Audit logs (admin-only endpoint)
export function useAuditLogs({ itemId = '', eventType = '', actor = '', limit = 50, offset = 0 } = {}, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    const normalizedItemId = itemId ? String(itemId).trim() : '';
    const normalizedEventType = eventType ? String(eventType).trim() : '';
    const normalizedActor = actor ? String(actor).trim() : '';
    const normalizedLimit = Number.isFinite(Number(limit)) ? Math.min(Math.max(parseInt(limit, 10), 1), 200) : 50;
    const normalizedOffset = Number.isFinite(Number(offset)) ? Math.max(parseInt(offset, 10), 0) : 0;

    const params = {
        itemId: normalizedItemId,
        eventType: normalizedEventType,
        actor: normalizedActor,
        limit: normalizedLimit,
        offset: normalizedOffset,
    };

    return useConfiguredQuery(
        queryKeys.auditLogs(params),
        async () => {
            const search = new URLSearchParams();
            if (normalizedItemId) search.set('item_id', normalizedItemId);
            if (normalizedEventType) search.set('event_type', normalizedEventType);
            if (normalizedActor) search.set('actor', normalizedActor);
            search.set('limit', String(normalizedLimit));
            search.set('offset', String(normalizedOffset));

            return apiFetch(authenticatedFetch, `/audit-logs?${search.toString()}`);
        },
        {
            staleTime: 0,
            refetchOnMount: 'always',
            ...options,
        }
    );
}

// Current user roles query
export function useUserRoles(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.userRoles,
        async () => {
            return apiFetch(authenticatedFetch, '/users/me/roles');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: 2,
            ...options,
        }
    );
}

// Convenience hook for role checking
export function useUserRole(options = {}) {
    const userRolesQuery = useUserRoles(options);
    
    return useMemo(() => {
        const roles = userRolesQuery.data?.map(roleObj => roleObj.role) || [];
        
        return {
            roles,
            isLoading: userRolesQuery.isLoading,
            error: userRolesQuery.error,
            hasRole: (roleName) => roles.includes(roleName)
        };
    }, [userRolesQuery.data, userRolesQuery.isLoading, userRolesQuery.error]);
}

// Tenant
export function useTenantInfo(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.tenant,
        async () => {
            return apiFetch(authenticatedFetch, '/tenant');
        },
        {
            staleTime: 1000 * 60 * 5, // 5 minutes
            ...options
        }
    );
}

export function useResetTenantTokens(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async () => {
            return apiFetch(authenticatedFetch, '/tenant/tokens/reset', {
                method: 'PATCH'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.tenant);
            },
            ...options
        }
    );
}

export function useResetTenantRequests(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async () => {
            return apiFetch(authenticatedFetch, '/tenant/requests/reset', {
                method: 'PATCH'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.tenant);
            },
            ...options
        }
    );
}

export function useUpdateTenantUserCap(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ user_count_limit }) => {
            return apiFetch(authenticatedFetch, '/tenant', {
                method: 'PATCH',
                body: JSON.stringify({ user_count_limit })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.tenant);
            },
            ...options
        }
    );
}

// Brainstorming-related hooks
export function useBrainstormingItems(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.brainstorming,
        async () => {
            return apiFetch(authenticatedFetch, '/brainstorming');
        },
        options
    );
}

export function useCreateBrainstormingItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ title, description, is_business_development }) => {
            return apiFetch(authenticatedFetch, '/brainstorming', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title, description, is_business_development })
            });
        },
        {
            onSuccess: () => {
                // Invalidate brainstorming queries to refresh the list
                queryClient.invalidateQueries({ queryKey: queryKeys.brainstorming });
            },
            ...options,
        }
    );
}

export function useUpdateBrainstormingItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, description, title, developed }) => {
            const payload = { description };
            if (title !== undefined) payload.title = title;
            if (developed !== undefined) payload.developed = developed;
            return apiFetch(authenticatedFetch, `/brainstorming/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.brainstorming });
            },
            ...options,
        }
    );
}

export function useUpdateBrainstormingItemAIAnalysis(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, aiAnalysis }) => {
            return apiFetch(authenticatedFetch, `/brainstorming/${id}/ai-analysis`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ aiAnalysis })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.brainstorming });
            },
            ...options,
        }
    );
}

export function useDeleteBrainstormingItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (id) => {
            return apiFetch(authenticatedFetch, `/brainstorming/${id}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.brainstorming });
            },
            ...options,
        }
    );
}

export function useAnalyzeIdeaWithAI(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ title, description }) => {
            return apiFetch(authenticatedFetch, '/ai-analysis/analyze-idea', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title, description })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.tenant);
            },
            onError: (error) => {
                if (error.status === 429) {
                    queryClient.invalidateQueries(queryKeys.tenant);
                }
            },
            ...options,
        }
    );
}

// Category and Subcategory hooks
export function useItemCategories(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.itemCategories,
        async () => {
            return apiFetch(authenticatedFetch, '/categories');
        },
        options
    );
}

export function useItemSubcategories(categoryId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.itemSubcategories(categoryId),
        async () => {
            return apiFetch(authenticatedFetch, `/categories/${categoryId}/subcategories`);
        },
        {
            // Only enable this query if a categoryId is provided
            enabled: !!categoryId,
            staleTime: 30 * 1000, // 30 seconds - subcategories don't change often
            ...options,
        }
    );
}

export function useAllSubcategories(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.allSubcategories,
        async () => {
            return apiFetch(authenticatedFetch, '/categories/subcategories/all');
        },
        {
            staleTime: 30 * 1000, // 30 seconds - subcategories don't change often
            ...options,
        }
    );
}

// Category and Subcategory Mutations

/**
 * Hook to create a new category
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateCategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (categoryData) => {
            return apiFetch(authenticatedFetch, '/categories', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(categoryData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
            },
            ...options
        }
    );
}

/**
 * Hook to update a category
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateCategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, categoryname, description }) => {
            return apiFetch(authenticatedFetch, `/categories/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ categoryname, description }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
            },
            ...options
        }
    );
}

/**
 * Hook to delete a category
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteCategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (categoryId) => {
            return apiFetch(authenticatedFetch, `/categories/${categoryId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
                queryClient.invalidateQueries({ queryKey: queryKeys.itemSubcategories });
            },
            ...options
        }
    );
}

/**
 * Hook to create a new subcategory
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateSubcategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ categoryId, subcategoryname, description }) => {
            return apiFetch(authenticatedFetch, `/categories/${categoryId}/subcategories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ subcategoryname, description }),
            });
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemSubcategories(variables.categoryId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
                queryClient.invalidateQueries({ queryKey: queryKeys.allSubcategories });
            },
            ...options
        }
    );
}

/**
 * Hook to update a subcategory
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateSubcategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ categoryId, id, subcategoryname, maincategoryid, description }) => {
            return apiFetch(authenticatedFetch, `/categories/${categoryId}/subcategories/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ subcategoryname, maincategoryid, description }),
            });
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemSubcategories(variables.categoryId) });
                if (variables.maincategoryid && variables.maincategoryid !== variables.categoryId) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.itemSubcategories(variables.maincategoryid) });
                }
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
                queryClient.invalidateQueries({ queryKey: queryKeys.allSubcategories });
            },
            ...options
        }
    );
}

/**
 * Hook to delete a subcategory
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteSubcategory(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ categoryId, subcategoryId }) => {
            return apiFetch(authenticatedFetch, `/categories/${categoryId}/subcategories/${subcategoryId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.itemSubcategories(variables.categoryId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.itemCategories });
                queryClient.invalidateQueries({ queryKey: queryKeys.allSubcategories });
            },
            ...options
        }
    );
}

// Mutation for developing an idea
export function useDevelopIdea(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (formData) => {
            return apiFetch(authenticatedFetch, '/items/develop', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
        },
        {
            onSuccess: () => {
                // Invalidate brainstorming queries to refresh the data
                queryClient.invalidateQueries({ queryKey: queryKeys.brainstorming });
                // Also invalidate items queries to show new items
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
            },
            ...options
        }
    );
}

// Items-related hooks
export function useItems(filters = {}, options = {}) {
    const { owner, owner_user_id, created_by, search, status } = filters;
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        [...queryKeys.items, { owner, owner_user_id, created_by, search, status }],
        async () => {
            const params = new URLSearchParams();
            if (owner) params.append('owner', owner);
            if (owner_user_id) params.append('owner_user_id', owner_user_id);
            if (created_by) params.append('created_by', created_by);
            if (search) params.append('search', search);
            if (status) params.append('status', status);
            
            return apiFetch(authenticatedFetch, `/items?${params.toString()}`);
        },
        options
    );
}

export function useItem(id, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.item(id),
        async () => {
            return apiFetch(authenticatedFetch, `/items/${id}`);
        },
        {
            enabled: !!id,
            retry: false, // Don't retry failed requests
            ...options
        }
    );
}

export function useItemUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.itemUsers,
        async () => {
            return apiFetch(authenticatedFetch, '/items/users');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
            ...options
        }
    );
}

// Hook to get active users for owner dropdown
export function useActiveUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.activeUsers,
        async () => {
            return apiFetch(authenticatedFetch, '/users/active');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
            ...options
        }
    );
}

// Hook to get endorser users for endorser dropdown
export function useEndorserUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.endorserUsers,
        async () => {
            return apiFetch(authenticatedFetch, '/users/endorsers');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
            ...options
        }
    );
}

// Hook to get all users for role management
export function useAllUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.users,
        async () => {
            return apiFetch(authenticatedFetch, '/users/active');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
            ...options
        }
    );
}

// Hook to get every user, active or not, for the admin role-management screen.
// useAllUsers hits /users/active, which filters out deactivated users and omits
// is_active entirely - fine for assignee pickers, useless for administering them.
export function useAdminUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        queryKeys.adminUsers,
        async () => {
            return apiFetch(authenticatedFetch, '/users');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - users don't change often
            ...options
        }
    );
}

// Hook to get roles for a specific user
export function useUserRolesForUser(userId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.usersByRole(userId),
        async () => {
            return apiFetch(authenticatedFetch, `/roles/user/${userId}`);
        },
        {
            enabled: !!userId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            ...options
        }
    );
}

// Hook to get all role assignments
export function useAllRoleAssignments(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.roles,
        async () => {
            return apiFetch(authenticatedFetch, '/roles');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

// Get role definitions from server
export function useRoleDefinitions(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['roleDefinitions'],
        async () => {
            return apiFetch(authenticatedFetch, '/roles/definitions');
        },
        {
            staleTime: 10 * 60 * 1000, // 10 minutes - role definitions change rarely
            ...options
        }
    );
}

// Check permission on server side
export function useCheckPermission() {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ requiredRole }) => {
            return apiFetch(authenticatedFetch, '/roles/check-permission', {
                method: 'POST',
                body: JSON.stringify({ requiredRole })
            });
        }
    );
}

// Mutation hook to assign a role to a user
export function useAssignRole() {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ userId, role }) => {
            return apiFetch(authenticatedFetch, '/roles/assign', {
                method: 'POST',
                body: JSON.stringify({ userId, role })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.roles });
                queryClient.invalidateQueries({ queryKey: queryKeys.userRoles });
            }
        }
    );
}

// Mutation hook to remove a role from a user
export function useRemoveRole() {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ userId, role }) => {
            return apiFetch(authenticatedFetch, '/roles/remove', {
                method: 'DELETE',
                body: JSON.stringify({ userId, role })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.roles });
                queryClient.invalidateQueries({ queryKey: queryKeys.userRoles });
            }
        }
    );
}

// Mutation hook to update a user's display name
export function useUpdateUserName() {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useConfiguredMutation(
        async ({ userId, name }) => {
            return apiFetch(authenticatedFetch, `/users/${userId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.users });
                queryClient.invalidateQueries({ queryKey: queryKeys.allUsers });
            }
        }
    );
}

// Mutation hook to deactivate a user
export function useDeactivateUser() {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ userId }) => {
            return apiFetch(authenticatedFetch, `/users/${userId}/deactivate`, {
                method: 'PATCH'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.users });
                queryClient.invalidateQueries({ queryKey: queryKeys.allUsers });
            }
        }
    );
}

// Mutation hook to activate a user
export function useActivateUser() {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ userId }) => {
            return apiFetch(authenticatedFetch, `/users/${userId}/activate`, {
                method: 'PATCH'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.users });
                queryClient.invalidateQueries({ queryKey: queryKeys.allUsers });
            }
        }
    );
}

// Mutation hook to invite users. Accepts a single { email } or { emails: [...] };
// the server sends one email per recipient and reports per-address results.
export function useInviteUser() {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ email, emails }) => {
            const payload = Array.isArray(emails) ? { emails } : { email };
            return apiFetch(authenticatedFetch, `/users/invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
        }
    );
}

// Hook for the child_of dropdown's candidate parents. Despite the endpoint
// name this returns closed items too, each carrying its status.
export function useOpenItems(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['openItems'],
        async () => {
            return apiFetch(authenticatedFetch, '/items/open');
        },
        {
            staleTime: 2 * 60 * 1000, // 2 minutes - more dynamic data
            ...options
        }
    );
}

export function useDeleteItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (itemId) => {
            return apiFetch(authenticatedFetch, `/items/${itemId}`, {
                method: 'DELETE'
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                // Invalidate items list to refresh after deletion
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useBulkDeleteItems(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async (itemIds = []) => {
            const ids = Array.isArray(itemIds)
                ? itemIds.map((v) => parseInt(v, 10)).filter((n) => Number.isFinite(n) && n > 0)
                : [];

            return apiFetch(authenticatedFetch, '/items/bulk-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids })
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useUpdateItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ itemId, updateData }) => {
            const itemCache = queryClient.getQueryData(queryKeys.item(itemId));
            const expectedUpdatedAt = updateData?.expected_updated_at || itemCache?.updated_at || null;
            const expectedRowVersion = updateData?.expected_row_version || itemCache?.row_version || null;
            return apiFetch(authenticatedFetch, `/items/${itemId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...updateData,
                    expected_updated_at: expectedUpdatedAt,
                    expected_row_version: expectedRowVersion,
                }),
            });
        },
        {
            onSuccess: async (...args) => {
                const [, variables] = args;
                const { itemId, fieldId, updateData } = variables;
                const itemIdKey = String(itemId);
                
                // Built-in query invalidation - always runs first
                // Always invalidate the specific item
                queryClient.invalidateQueries({ queryKey: queryKeys.item(itemId) });

                // Stale Items report depends on item activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
                
                // Invalidate custom field values for this item
                queryClient.invalidateQueries({ queryKey: ['customFieldValues', itemId] });
                
                // If approval_status was updated, invalidate approval-related queries
                if (updateData && updateData.approval_status !== undefined) {
                    queryClient.invalidateQueries({ queryKey: ['approvals'] });
                    queryClient.invalidateQueries({ queryKey: ['currentStageApprovalRequirements'] });
                    // Approval status changes can trigger auto-advancement which creates risk snapshots
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                }
                
                // Only invalidate items list for changes that truly affect list filtering
                // Most field updates don't need to refresh the entire items list
                if (fieldId && ['status', 'current_stage', 'approval_status'].includes(fieldId)) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.items });
                    
                    // Stage changes create risk snapshots
                    if (fieldId === 'current_stage') {
                        queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                        queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                    }
                }
                
                // Then call user onSuccess if provided
                if (options.onSuccess) {
                    await options.onSuccess(...args);
                }
            },
            onError: options.onError,
            onMutate: options.onMutate,
            onSettled: options.onSettled,
        }
    );
}

export function useAdvanceItemStage(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ itemId, stageData }) => {
            const itemCache = queryClient.getQueryData(queryKeys.item(itemId));
            const expectedUpdatedAt = stageData?.expected_updated_at || itemCache?.updated_at || null;
            const expectedRowVersion = stageData?.expected_row_version || itemCache?.row_version || null;
            return apiFetch(authenticatedFetch, `/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...stageData,
                    expected_updated_at: expectedUpdatedAt,
                    expected_row_version: expectedRowVersion,
                }),
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                const [, variables] = args;
                const itemIdKey = String(variables.itemId);
                // Invalidate all related queries for stage advancement
                queryClient.invalidateQueries({ queryKey: queryKeys.item(variables.itemId) });
                queryClient.invalidateQueries({ queryKey: ['customFieldValues', variables.itemId] });
                queryClient.invalidateQueries({ queryKey: ['approvals'] });
                queryClient.invalidateQueries({ queryKey: ['currentStageApprovalRequirements'] });
                // Stage changes affect items list filtering, so invalidate it
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                // Stage advances create risk snapshots, so invalidate risk snapshot data
                queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useRevertItemStage(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ itemId, stageData }) => {
            const itemCache = queryClient.getQueryData(queryKeys.item(itemId));
            const expectedUpdatedAt = stageData?.expected_updated_at || itemCache?.updated_at || null;
            const expectedRowVersion = stageData?.expected_row_version || itemCache?.row_version || null;
            return apiFetch(authenticatedFetch, `/items/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...stageData,
                    expected_updated_at: expectedUpdatedAt,
                    expected_row_version: expectedRowVersion,
                }),
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                const [, variables] = args;
                const itemIdKey = String(variables.itemId);
                // Back to invalidating for now to avoid blank fields
                queryClient.invalidateQueries({ queryKey: queryKeys.item(variables.itemId) });
                // Stage changes affect items list filtering, so invalidate it  
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                // Stage reverts also create risk snapshots, so invalidate risk snapshot data
                queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

// Workflow Stages Queries
export function useWorkflowStages(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.workflowStages,
        async () => {
            return apiFetch(authenticatedFetch, '/workflow_stages');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - stages don't change often
            ...options
        }
    );
}

export function useCreateWorkflowStage(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (stageData) => {
            return apiFetch(authenticatedFetch, '/workflow_stages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(stageData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.workflowStages });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useUpdateWorkflowStage(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, ...stageData }) => {
            return apiFetch(authenticatedFetch, `/workflow_stages/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(stageData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.workflowStages });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useDeleteWorkflowStage(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (stageId) => {
            return apiFetch(authenticatedFetch, `/workflow_stages/${stageId}`, {
                method: 'DELETE'
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.workflowStages });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useReorderWorkflowStages(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (stages) => {
            return apiFetch(authenticatedFetch, '/workflow_stages/reorder', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stages })
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.workflowStages });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

// Stage Field Mappings Queries
export function useStageFieldMappings(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.stageFieldMappings,
        async () => {
            try {
                const data = await apiFetch(authenticatedFetch, '/stage-field-mappings');
                return data;
            } catch (error) {
                // If endpoint doesn't exist yet, return empty structure
                if (error.status === 404) return { mappings: [], subsections: [] };
                throw error;
            }
        },
        {
            staleTime: 2 * 60 * 1000, // 2 minutes - mappings change moderately
            ...options
        }
    );
}

export function useUpdateStageFieldMappings(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (data) => {
            return apiFetch(authenticatedFetch, '/stage-field-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.stageFieldMappings });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

// Choice data hooks
export function useDetectabilityChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.detectabilityChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/detectability');
        },
        options
    );
}

export function useScheduleChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.scheduleChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/schedule');
        },
        options
    );
}

export function useOriginatingDisciplineChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.originatingDisciplineChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/originating-discipline');
        },
        options
    );
}

export function useRootCauseCategoryChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.rootCauseCategoryChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/root-cause-category');
        },
        options
    );
}

export function useProbabilityChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.probabilityChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/probability');
        },
        options
    );
}

export function useCostChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.costChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/cost');
        },
        options
    );
}

export function useAllChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.allChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/all');
        },
        options
    );
}

export function useOpportunityThreatChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.opportunityThreatChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/opportunity-threat');
        },
        options
    );
}

export function useTypeChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.typeChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/type');
        },
        options
    );
}

export function useRiskTypeChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.riskTypeChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/risk-type');
        },
        options
    );
}

export function useAssignedDisciplineChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.assignedDisciplineChoices,
        async () => {
            return apiFetch(authenticatedFetch, '/choices/assigned-discipline');
        },
        options
    );
}

// Originating Discipline Choice Mutations

/**
 * Hook to create a new originating discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateOriginatingDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/originating-discipline', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.originatingDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update an originating discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateOriginatingDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, originating_discipline, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/originating-discipline/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ originating_discipline, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.originatingDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete an originating discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteOriginatingDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/originating-discipline/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.originatingDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder originating discipline choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderOriginatingDisciplineChoices(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/originating-discipline/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.originatingDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Root Cause Category Choice Mutations

/**
 * Hook to create a new root cause category choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateRootCauseCategoryChoice(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/root-cause-category', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.rootCauseCategoryChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update a root cause category choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateRootCauseCategoryChoice(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useConfiguredMutation(
        async ({ id, root_cause_category, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/root-cause-category/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ root_cause_category, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.rootCauseCategoryChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete a root cause category choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteRootCauseCategoryChoice(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/root-cause-category/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.rootCauseCategoryChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder root cause category choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderRootCauseCategoryChoices(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/root-cause-category/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.rootCauseCategoryChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Opportunity/Threat Choice Mutations

/**
 * Hook to create a new opportunity/threat choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateOpportunityThreatChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/opportunity-threat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.opportunityThreatChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update an opportunity/threat choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateOpportunityThreatChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, opportunity_threat, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/opportunity-threat/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ opportunity_threat, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.opportunityThreatChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete an opportunity/threat choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteOpportunityThreatChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/opportunity-threat/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.opportunityThreatChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder opportunity/threat choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderOpportunityThreatChoices(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/opportunity-threat/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.opportunityThreatChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Type Choice Mutations

/**
 * Hook to create a new type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/type', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.typeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update a type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, type, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/type/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ type, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.typeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete a type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/type/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.typeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder type choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderTypeChoices(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/type/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.typeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Risk Type Choice Mutations

/**
 * Hook to create a new risk type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateRiskTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/risk-type', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.riskTypeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update a risk type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateRiskTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, risk_type, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/risk-type/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ risk_type, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.riskTypeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete a risk type choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteRiskTypeChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/risk-type/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.riskTypeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder risk type choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderRiskTypeChoices(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/risk-type/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.riskTypeChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Assigned Discipline Choice Mutations

/**
 * Hook to create a new assigned discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useCreateAssignedDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceData) => {
            return apiFetch(authenticatedFetch, '/choices/assigned-discipline', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(choiceData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.assignedDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to update an assigned discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useUpdateAssignedDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, assigned_discipline, display_order }) => {
            return apiFetch(authenticatedFetch, `/choices/assigned-discipline/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ assigned_discipline, display_order }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.assignedDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to delete an assigned discipline choice
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useDeleteAssignedDisciplineChoice(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choiceId) => {
            return apiFetch(authenticatedFetch, `/choices/assigned-discipline/${choiceId}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.assignedDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

/**
 * Hook to reorder assigned discipline choices
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object with mutate, mutateAsync, isPending, etc.
 */
export function useReorderAssignedDisciplineChoices(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (choices) => {
            return apiFetch(authenticatedFetch, '/choices/assigned-discipline/reorder', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.assignedDisciplineChoices });
                queryClient.invalidateQueries({ queryKey: queryKeys.allChoices });
            },
            ...options
        }
    );
}

// Task hooks
export function useTasks(filters = {}, options = {}) {
    const { assigned_to_user_id, assigned_to, status } = filters;
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        [...queryKeys.tasks, { assigned_to_user_id, assigned_to, status }],
        async () => {
            const params = new URLSearchParams();
            if (assigned_to_user_id) params.append('assigned_to_user_id', assigned_to_user_id);
            if (assigned_to) params.append('assigned_to', assigned_to);
            if (status) params.append('status', status);
            
            return apiFetch(authenticatedFetch, `/tasks?${params.toString()}`);
        },
        options
    );
}

export function useTasksByParent(parentId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.tasksByParent(parentId),
        async () => {
            return apiFetch(authenticatedFetch, `/tasks/parent/${parentId}`);
        },
        {
            enabled: !!parentId,
            ...options
        }
    );
}

export function useTask(taskId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.task(taskId),
        async () => {
            return apiFetch(authenticatedFetch, `/tasks/${taskId}`);
        },
        {
            enabled: !!taskId,
            ...options
        }
    );
}

export function useCreateTask(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (taskData) => {
            return apiFetch(authenticatedFetch, '/tasks', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(taskData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
                queryClient.invalidateQueries({ queryKey: queryKeys.tasksByParent });

                // Stale Items report depends on task activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
            },
            ...options
        }
    );
}

export function useCreateItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (itemData = {}) => {
            return apiFetch(authenticatedFetch, '/items', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(itemData),
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                queryClient.invalidateQueries({ queryKey: ['openItems'] });
            },
            ...options
        }
    );
}

export function useUpdateTask(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, ...taskData }) => {
            return apiFetch(authenticatedFetch, `/tasks/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(taskData),
            });
        },
        {
            onSuccess: async (data, variables) => {
                // Built-in query invalidation - always runs first
                queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
                queryClient.invalidateQueries({ queryKey: queryKeys.task(data.id) });
                queryClient.invalidateQueries({ queryKey: queryKeys.tasksByParent(data.parent_id) });

                // Stale Items report depends on task activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
                
                // Then call user onSuccess if provided
                if (options.onSuccess) {
                    await options.onSuccess(data, variables);
                }
            },
            onError: options.onError,
            onMutate: options.onMutate,
            onSettled: options.onSettled,
        }
    );
}

export function useDeleteTask(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (id) => {
            return apiFetch(authenticatedFetch, `/tasks/${id}`, {
                method: 'DELETE',
            });
        },
        {
            onSuccess: async (data, variables) => {
                // Built-in query invalidation - always runs first  
                queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
                queryClient.invalidateQueries({ queryKey: queryKeys.tasksByParent });

                // Stale Items report depends on task activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
                
                // Then call user onSuccess if provided
                if (options.onSuccess) {
                    await options.onSuccess(data, variables);
                }
            },
            onError: options.onError,
            onMutate: options.onMutate,
            onSettled: options.onSettled,
        }
    );
}

export function useUpdateTaskStatus(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, status }) => {
            return apiFetch(authenticatedFetch, `/tasks/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status }),
            });
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
                queryClient.invalidateQueries({ queryKey: queryKeys.task(data.id) });
                queryClient.invalidateQueries({ queryKey: queryKeys.tasksByParent(data.parent_id) });

                // Stale Items report depends on task activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
            },
            ...options
        }
    );
}

export function useSendTaskReminder(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, comment }) => {
            return apiFetch(authenticatedFetch, `/tasks/${id}/send-reminder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ comment }),
            });
        },
        {
            onSuccess: (data) => {
                if (options.onSuccess) {
                    options.onSuccess(data);
                }
            },
            onError: (error) => {
                if (options.onError) {
                    options.onError(error);
                }
            },
            ...options
        }
    );
}

// Task Comments hooks
export function useTaskComments(taskId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.taskComments(taskId),
        async () => {
            return apiFetch(authenticatedFetch, `/task-comments/${taskId}`);
        },
        {
            enabled: !!taskId,
            staleTime: 30 * 1000, // 30 seconds
            ...options
        }
    );
}

export function useCreateTaskComment(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ task_id, comment_text, author_name, mentioned_users = [] }) => {
            return apiFetch(authenticatedFetch, '/task-comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ task_id, comment_text, author_name, mentioned_users })
            });
        },
        {
            onSuccess: (data, variables) => {
                // Invalidate the task comments query to refetch
                queryClient.invalidateQueries({ queryKey: queryKeys.taskComments(variables.task_id) });

                // Stale Items report depends on task comment activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
            },
            ...options
        }
    );
}

export function useDeleteTaskComment(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ commentId, taskId }) => {
            return apiFetch(authenticatedFetch, `/task-comments/${commentId}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: (data, variables) => {
                // Invalidate the task comments query to refetch
                queryClient.invalidateQueries({ queryKey: queryKeys.taskComments(variables.taskId) });

                // Stale Items report depends on task comment activity
                queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
            },
            ...options
        }
    );
}

// Document hooks
export function useDocuments(itemId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.documentsByItem(itemId),
        async () => {
            return apiFetch(authenticatedFetch, `/documents/item/${itemId}`);
        },
        {
            enabled: !!itemId,
            ...options
        }
    );
}

// Document versioning hooks
export function useCheckDuplicateDocument(itemId, filename, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    const enabled = !!itemId && !!filename && (options.enabled ?? true);

    return useConfiguredQuery(
        queryKeys.documentDuplicate(itemId, filename),
        async () => {
            const encodedFilename = encodeURIComponent(filename);
            return apiFetch(authenticatedFetch, `/documents/check-duplicate/${itemId}/${encodedFilename}`);
        },
        {
            enabled,
            staleTime: 1000 * 30,
            ...options,
        }
    );
}

export function useDocumentVersions(documentId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        queryKeys.documentVersions(documentId),
        async () => {
            return apiFetch(authenticatedFetch, `/documents/${documentId}/versions`);
        },
        {
            enabled: !!documentId,
            ...options,
        }
    );
}

export function useDocumentVersionCount(documentId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        ['documents', 'version-count', documentId],
        async () => {
            return apiFetch(authenticatedFetch, `/documents/${documentId}/version-count`);
        },
        {
            enabled: !!documentId,
            staleTime: 1000 * 60, // 1 minute
            ...options,
        }
    );
}

export function useDocumentVersionCounts(documentIds = [], options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    const enabled = (options.enabled ?? true) && Array.isArray(documentIds) && documentIds.length > 0;
    const uniqueIds = useMemo(() => {
        if (!enabled) return [];
        return Array.from(new Set(documentIds.map((id) => parseInt(id, 10)).filter(Number.isFinite)));
    }, [enabled, documentIds]);

    const queries = useQueries({
        queries: uniqueIds.map((documentId) => ({
            queryKey: ['documents', 'version-count', documentId],
            queryFn: async () => apiFetch(authenticatedFetch, `/documents/${documentId}/version-count`),
            enabled: enabled && !!documentId,
            staleTime: 1000 * 60,
        })),
    });

    const byId = useMemo(() => {
        const map = {};
        for (let i = 0; i < uniqueIds.length; i++) {
            const id = uniqueIds[i];
            const q = queries[i];
            const count = q?.data?.version_count;
            if (Number.isFinite(count)) {
                map[id] = count;
            }
        }
        return map;
    }, [uniqueIds, queries]);

    const isLoading = queries.some((q) => q.isLoading);
    const error = queries.find((q) => q.isError)?.error || null;

    return {
        byId,
        isLoading,
        error,
        queries,
    };
}

export function useRestoreDocumentVersion(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ documentId, versionId }) => {
            return apiFetch(authenticatedFetch, `/documents/${documentId}/restore-version/${versionId}`, {
                method: 'POST',
            });
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.documents });
                if (variables?.documentId) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.document(variables.documentId) });
                    queryClient.invalidateQueries({ queryKey: queryKeys.documentVersions(variables.documentId) });
                }
            },
            ...options,
        }
    );
}

export function useCreateDocument(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ itemId, file, notes, isNewVersion = false, existingDocumentId = null }) => {
            // Be tolerant of File-like objects (cross-window instanceof can be unreliable)
            if (!file || typeof file !== 'object' || typeof file.name !== 'string') {
                throw new Error('Invalid file object provided to useCreateDocument');
            }

            const wantsNewVersion = !!isNewVersion;
            if (wantsNewVersion && !existingDocumentId) {
                throw new Error('existingDocumentId is required when isNewVersion=true');
            }
            
            const formData = new FormData();
            formData.append('file', file);
            if (itemId != null) formData.append('item_id', itemId);
            if (notes) formData.append('notes', notes);

            if (wantsNewVersion) {
                formData.append('isNewVersion', 'true');
                formData.append('existingDocumentId', String(existingDocumentId));
            }
            
            return apiFetch(authenticatedFetch, '/documents', {
                method: 'POST',
                body: formData
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.documents });
            },
            ...options
        }
    );
}

export function useDeleteDocument(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (id) => {
            return apiFetch(authenticatedFetch, `/documents/${id}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.documents });
            },
            ...options
        }
    );
}

export function useUpdateDocumentNotes(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, notes }) => {
            return apiFetch(authenticatedFetch, `/documents/${id}/notes`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ notes })
            });
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.document(data.id) });
                queryClient.invalidateQueries({ queryKey: queryKeys.documents });
            },
            ...options
        }
    );
}

export function useDownloadDocument() {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return async (documentId, filename) => {
        try {
            const response = await authenticatedFetch(apiUrl(`/documents/${documentId}/download`));
            
            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            throw error;
        }
    };
}

export function useDownloadDocumentVersion() {
    const authenticatedFetch = useAuthenticatedFetch();

    return async (documentId, versionId, filename, versionNumber) => {
        try {
            const response = await authenticatedFetch(apiUrl(`/documents/${documentId}/versions/${versionId}/download`));

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const name = filename || 'document';
            const dotIndex = name.lastIndexOf('.');
            const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
            const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
            const downloadName = `${base}_v${versionNumber}${ext}`;

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = downloadName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Version download failed:', error);
            throw error;
        }
    };
}

// Import-related hooks
export const useDownloadImportTemplate = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useMutation({
        mutationFn: async (formatInput) => {
            const format = String(formatInput || 'xlsx').trim().toLowerCase() === 'csv' ? 'csv' : 'xlsx';
                        const templateUrl = apiUrl(`/items/import/template?format=${encodeURIComponent(format)}`);

            const response = await authenticatedFetch(templateUrl, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

            // Defensive: if the API accidentally returns HTML/JSON (auth error, proxy misroute, etc),
            // the browser will still download it as .xlsx and Excel will say “invalid format”.
            const contentType = (response.headers.get('content-type') || '').toLowerCase();
            const looksWrongForXlsx = format === 'xlsx' && (contentType.includes('text/html') || contentType.includes('application/json'));
            const looksWrongForCsv = format === 'csv' && contentType.includes('text/html');
            if (looksWrongForXlsx || looksWrongForCsv) {
                const bodyText = await response.text();
                const preview = bodyText.slice(0, 200);
                throw new Error(`Template download returned ${contentType || 'unknown content type'} instead of a file. Preview: ${preview}`);
            }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a download link
    const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
    a.href = downloadUrl;
            a.download = `items-import-template.${format}`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
    window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
            return { success: true, format };
    }
  });
};

export const usePreviewImport = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

            const url = apiUrl('/items/import?preview=true');
            const timeoutMs = 60000;
            const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const timeoutHandle = abortController
                ? setTimeout(() => {
                        abortController.abort();
                    }, timeoutMs)
                : null;

            let response;
            try {
                response = await authenticatedFetch(url, {
        method: 'POST',
        body: formData,
                    signal: abortController?.signal
                });
            } catch (error) {
                if (error?.name === 'AbortError') {
                    throw new Error(`Preview timed out after ${Math.round(timeoutMs / 1000)} seconds. Check server import logs for the last phase/heartbeat entry.`);
                }
                throw error;
            } finally {
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle);
                }
            }
      
      if (!response.ok) {
                let message = `Preview failed (HTTP ${response.status})`;
                try {
                    const errorJson = await response.json();
                    const baseMessage = errorJson?.error || errorJson?.message || message;
                    const details = errorJson?.details ? String(errorJson.details) : '';
                    const step = errorJson?.step ? String(errorJson.step) : '';
                    message = details ? `${baseMessage} (${details}${step ? `; step: ${step}` : ''})` : baseMessage;
                } catch {
                    try {
                        const errorText = await response.text();
                        if (errorText) message = errorText;
                    } catch {
                        // ignore
                    }
                }
                throw new Error(message);
      }
      
      return await response.json();
    }
  });
};

export const useImportItems = () => {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, ownerSelections, projectSelections }) => {
            const formData = new FormData();
            formData.append('file', file);
            if (ownerSelections && ownerSelections.length > 0) {
                formData.append('ownerSelections', JSON.stringify(ownerSelections));
            }
            if (projectSelections && projectSelections.length > 0) {
                formData.append('projectSelections', JSON.stringify(projectSelections));
            }

            const url = apiUrl('/items/import');

            const response = await authenticatedFetch(url, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                let message = `Import failed (HTTP ${response.status})`;
                try {
                    const errorJson = await response.json();
                    const baseMessage = errorJson?.error || errorJson?.message || message;
                    const details = errorJson?.details ? String(errorJson.details) : '';
                    const step = errorJson?.step ? String(errorJson.step) : '';
                    message = details ? `${baseMessage} (${details}${step ? `; step: ${step}` : ''})` : baseMessage;
                } catch {
                    try {
                        const errorText = await response.text();
                        if (errorText) message = errorText;
                    } catch {
                        // ignore
                    }
                }
                throw new Error(message);
            }

            return await response.json();
        },
        onSuccess: () => {
            // Invalidate items queries to refresh the list
            queryClient.invalidateQueries(['items']);
        }
    });
};

// Risk Matrix queries
export function useRiskMatrix() {
    const authenticatedFetch = useAuthenticatedFetch();
    const { accounts, inProgress } = useMsal();
    
    return useConfiguredQuery(
        queryKeys.riskMatrix,
        async () => {
            return apiFetch(authenticatedFetch, '/risk-matrix');
        },
        { 
            enabled: inProgress === 'none' && accounts && accounts.length > 0,
            staleTime: 1000 * 60 * 10, // 10 minutes - risk matrix doesn't change often
            retry: 3
        }
    );
}

export function useUpdateRiskMatrix(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (config) => {
            return apiFetch(authenticatedFetch, '/risk-matrix', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: queryKeys.riskMatrix });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

// Risk Snapshots queries
/**
 * Query hook to fetch all risk snapshots for an item
 * @param {number|string} itemId - The item ID to fetch snapshots for
 * @returns {Object} React Query result with snapshots array
 */
export function useRiskSnapshots(itemId) {
    const authenticatedFetch = useAuthenticatedFetch();
    const itemIdKey = String(itemId);
    
    return useConfiguredQuery(
        ['riskSnapshots', itemIdKey],
        async () => {
            return apiFetch(authenticatedFetch, `/risk-snapshots/${itemId}`);
        },
        {
            enabled: !!itemId,
            staleTime: 1000 * 60 * 5, // 5 minutes
            select: (data) => data || []
        }
    );
}

/**
 * Query hook to fetch the most recent risk snapshot for an item
 * @param {number|string} itemId - The item ID to fetch latest snapshot for
 * @returns {Object} React Query result with latest snapshot
 */
export function useLatestRiskSnapshot(itemId) {
    const authenticatedFetch = useAuthenticatedFetch();
    const itemIdKey = String(itemId);
    
    return useConfiguredQuery(
        ['riskSnapshots', itemIdKey, 'latest'],
        async () => {
            return apiFetch(authenticatedFetch, `/risk-snapshots/${itemId}/latest`);
        },
        {
            enabled: !!itemId,
            staleTime: 1000 * 60 * 5, // 5 minutes
        }
    );
}

/**
 * Query hook to fetch comparison between two risk snapshots
 * @param {number|string} itemId - The item ID
 * @param {number|string} snapshot1Id - First snapshot ID
 * @param {number|string} snapshot2Id - Second snapshot ID
 * @returns {Object} React Query result with comparison data
 */
export function useRiskSnapshotComparison(itemId, snapshot1Id, snapshot2Id) {
    const authenticatedFetch = useAuthenticatedFetch();
    const itemIdKey = String(itemId);
    
    return useConfiguredQuery(
        ['riskSnapshots', itemIdKey, 'compare', snapshot1Id, snapshot2Id],
        async () => {
            return apiFetch(authenticatedFetch, `/risk-snapshots/compare/${snapshot1Id}/${snapshot2Id}`);
        },
        {
            enabled: !!(itemId && snapshot1Id && snapshot2Id),
            staleTime: 1000 * 60 * 10, // 10 minutes - comparisons are more stable
        }
    );
}

/**
 * Mutation hook to delete a risk snapshot
 * @param {Object} options - Optional mutation options
 * @returns {Object} React Query mutation result
 */
export function useDeleteRiskSnapshot(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (snapshotId) => {
            return apiFetch(authenticatedFetch, `/risk-snapshots/${snapshotId}`, {
                method: 'DELETE'
            });
        },
        {
            ...options,
            onSuccess: (data, snapshotId) => {
                // Get itemId from the response to invalidate the correct queries
                const itemId = data?.itemId;
                if (itemId) {
                    const itemIdKey = String(itemId);
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                }
                if (options.onSuccess) {
                    options.onSuccess(data, snapshotId);
                }
            }
        }
    );
}

// Approval Queries
export function useApprovals(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['approvals', options.stage_id, options.item_id, options.status],
        async () => {
            const params = new URLSearchParams();
            if (options.stage_id) params.append('stage_id', options.stage_id);
            if (options.item_id) params.append('item_id', options.item_id);
            if (options.status) params.append('status', options.status);
            
            const url = `/approvals${params.toString() ? `?${params.toString()}` : ''}`;
            return apiFetch(authenticatedFetch, url);
        },
        {
            staleTime: 2 * 60 * 1000, // 2 minutes
            ...options
        }
    );
}

export function useActionableApprovalsCount(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        ['approvals', 'actionable-count'],
        async () => {
            return apiFetch(authenticatedFetch, '/approvals/actionable-count');
        },
        {
            staleTime: 60 * 1000, // 1 minute
            ...options
        }
    );
}

export function useApproval(approvalId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['approval', approvalId],
        async () => {
            return apiFetch(authenticatedFetch, `/approvals/${approvalId}`);
        },
        {
            enabled: !!approvalId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            ...options
        }
    );
}

export function useApprovalRequirements(stageId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['approvalRequirements', stageId],
        async () => {
            return apiFetch(authenticatedFetch, `/approvals/requirements/${stageId}`);
        },
        {
            enabled: !!stageId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

// Eligible approvers (users grouped by role) for a stage's role-based requirements.
export function useEligibleApprovers(stageId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        ['eligibleApprovers', stageId],
        async () => {
            return apiFetch(authenticatedFetch, `/approvals/eligible-approvers/${stageId}`);
        },
        {
            enabled: !!stageId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

export function useCreateApprovalRequirement(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async (requirementData) => {
            return apiFetch(authenticatedFetch, '/approvals/requirement', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requirementData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: ['approvalRequirements'] });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useDeleteApprovalRequirement(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (requirementData) => {
            return apiFetch(authenticatedFetch, '/approvals/requirement', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requirementData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                queryClient.invalidateQueries({ queryKey: ['approvalRequirements'] });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useCreateApproval(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (approvalData) => {
            return apiFetch(authenticatedFetch, '/approvals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(approvalData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                const [, variables] = args;
                // Invalidate approval queries
                queryClient.invalidateQueries({ queryKey: ['approvals'] });
                // If we have item_id in the approval data, invalidate that specific item
                if (variables && variables.item_id) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.item(variables.item_id) });
                }
                // Approval creation might affect items list (approval status changes)
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useUpdateApproval(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, ...approvalData }) => {
            return apiFetch(authenticatedFetch, `/approvals/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(approvalData)
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                const [updatedApproval, variables] = args;
                // Invalidate approval queries
                queryClient.invalidateQueries({ queryKey: ['approvals'] });
                // If we have item_id in the approval data or response, invalidate that specific item's data
                const itemId = updatedApproval?.item_id || variables?.item_id;
                if (itemId) {
                    const itemIdKey = String(itemId);
                    queryClient.invalidateQueries({ queryKey: queryKeys.item(itemId) });
                    // Invalidate risk snapshots for the item since auto-advancement creates new snapshots
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey] });
                    queryClient.invalidateQueries({ queryKey: ['riskSnapshots', itemIdKey, 'latest'] });
                }
                // Approval updates affect items list (approval status changes trigger auto-advancement)
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

export function useDeleteApproval(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ id, itemId }) => {
            return apiFetch(authenticatedFetch, `/approvals/${id}`, {
                method: 'DELETE'
            });
        },
        {
            ...options,
            onSuccess: (...args) => {
                const [, variables] = args;
                queryClient.invalidateQueries({ queryKey: ['approvals'] });
                if (variables?.itemId) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.item(variables.itemId) });
                }
                queryClient.invalidateQueries({ queryKey: queryKeys.items });
                if (options.onSuccess) {
                    options.onSuccess(...args);
                }
            }
        }
    );
}

// User Views hooks
export function useUserViews(tableName, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.userViews(tableName),
        async () => {
            return apiFetch(authenticatedFetch, `/user-views/${tableName}`);
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            enabled: !!tableName && (tableName === 'items' || tableName === 'tasks'),
            ...options
        }
    );
}

export function useCreateView(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (viewData) => {
            return apiFetch(authenticatedFetch, '/user-views', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(viewData)
            });
        },
        {
            onSuccess: (data) => {
                // Invalidate user views for the specific table
                if (data.table_name) {
                    queryClient.invalidateQueries({ 
                        queryKey: queryKeys.userViews(data.table_name) 
                    });
                }
                // Also invalidate all user views queries as a fallback
                queryClient.invalidateQueries({ 
                    queryKey: ['user-views'] 
                });
            },
            ...options
        }
    );
}

export function useUpdateView(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, ...updateData }) => {
            return apiFetch(authenticatedFetch, `/user-views/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });
        },
        {
            onSuccess: (data) => {
                // Invalidate user views for the specific table
                if (data.table_name) {
                    queryClient.invalidateQueries({ 
                        queryKey: queryKeys.userViews(data.table_name) 
                    });
                }
                // Also invalidate all user views queries as a fallback
                queryClient.invalidateQueries({ 
                    queryKey: ['user-views'] 
                });
            },
            ...options
        }
    );
}

// The default view is a per-user preference, not a property of the view, so it has
// its own endpoint. Pass { tableName, viewId } - a null viewId falls back to the
// built-in "All Items"/"All Tasks" view.
export function useSetDefaultView(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ tableName, viewId }) => {
            return apiFetch(authenticatedFetch, `/user-views/${tableName}/default`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ view_id: viewId ?? null })
            });
        },
        {
            onSuccess: (data, variables) => {
                const tableName = data?.table_name || variables?.tableName;
                if (tableName) {
                    queryClient.invalidateQueries({
                        queryKey: queryKeys.userViews(tableName)
                    });
                }
                queryClient.invalidateQueries({
                    queryKey: ['user-views']
                });
            },
            ...options
        }
    );
}

export function useDeleteView(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (viewId) => {
            return apiFetch(authenticatedFetch, `/user-views/${viewId}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: (data) => {
                // Invalidate user views for the specific table if available
                if (data.data?.table_name) {
                    queryClient.invalidateQueries({ 
                        queryKey: queryKeys.userViews(data.data.table_name) 
                    });
                }
                // Also invalidate all user views queries as a fallback
                queryClient.invalidateQueries({ 
                    queryKey: ['user-views'] 
                });
            },
            ...options
        }
    );
}

export function useUsers(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.users,
        async () => {
            return apiFetch(authenticatedFetch, '/users/active');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

// Projects hooks
export function useProjects(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['projects'],
        async () => {
            return apiFetch(authenticatedFetch, '/projects');
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

export function useProject(projectId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['projects', projectId],
        async () => {
            return apiFetch(authenticatedFetch, `/projects/${projectId}`);
        },
        {
            enabled: !!projectId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

export function useCreateProject(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (projectData) => {
            return apiFetch(authenticatedFetch, '/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectData)
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
            },
            ...options
        }
    );
}

export function useUpdateProject(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ id, ...projectData }) => {
            return apiFetch(authenticatedFetch, `/projects/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectData)
            });
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
                queryClient.invalidateQueries({ queryKey: ['projects', data.id] });
            },
            ...options
        }
    );
}

export function useDeleteProject(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (projectId) => {
            return apiFetch(authenticatedFetch, `/projects/${projectId}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['projects'] });
            },
            ...options
        }
    );
}

// Cost Impact Configuration
export function useCostImpactConfig(projectId, options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        ['projects', projectId, 'cost-impact-config'],
        () => apiFetch(authenticatedFetch, `/projects/${projectId}/cost-impact-config`),
        {
            enabled: !!projectId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
}

export function useUpdateCostImpactConfig(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();
    
    return useConfiguredMutation(
        async ({ projectId, config }) => {
            return apiFetch(authenticatedFetch, `/projects/${projectId}/cost-impact-config`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(config)
            });
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'cost-impact-config'] });
                queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
            },
            ...options
        }
    );
}

// PursuitConnect Pursuits
export function usePursuitConnectStatus(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredQuery(
        queryKeys.pursuitConnectStatus,
        async () => {
            return apiFetch(authenticatedFetch, '/pursuit-connect/status');
        },
        {
            staleTime: 1000 * 60 * 5,
            ...options
        }
    );
}

export function usePursuitConnectProjects() {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useQuery({
        queryKey: ['pursuit-connect-pursuits'],
        queryFn: async () => {
            return apiFetch(authenticatedFetch, '/pursuit-connect/pursuits');
        },
        enabled: false, // Only run when manually triggered
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// ============================================================================
// AUTHENTICATION & MEDIA HOOKS - Centralized authentication & blob management
// ============================================================================

/**
 * Hook for creating authenticated blob URLs for media elements
 * Manages blob creation, caching, loading states, and cleanup
 * @param {string} containerName - Azure blob container name
 * @param {string} blobName - Azure blob name
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with url, loading state, and error handling
 */
export const useAuthenticatedBlobUrl = (containerName, blobName, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  // Determine query key and URL
  const queryKey = ['authenticated-blob-url', containerName, blobName];
  const fetchUrl = containerName && blobName ? `/azure/blob/${containerName}/${blobName}` : null;
  
  const { 
    enabled = true, 
    staleTime = 60 * 60 * 1000, // 1 hour - media files rarely change
    gcTime = 4 * 60 * 60 * 1000, // 4 hours - keep in cache longer
    refetchOnWindowFocus = false,
    refetchOnMount = false,
    ...customOptions 
  } = options;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!fetchUrl) {
        throw new Error('Invalid blob URL configuration - missing container or blob name');
      }

      try {
        console.log(`[Blob URL] Fetching authenticated blob from: ${fetchUrl}`);
        
        // Fetch the blob data with proper authentication
        const response = await authenticatedFetch(apiUrl(fetchUrl));
        
        if (!response.ok) {
          console.error(`[Blob URL] Failed to fetch blob from ${fetchUrl}: ${response.status} ${response.statusText}`);
          throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
        }
        
        console.log(`[Blob URL] Successfully fetched blob from: ${fetchUrl}`);

        const blob = await response.blob();
        
        // Create object URL for the blob
        const objectUrl = URL.createObjectURL(blob);
        
        // Store cleanup function in query data
        return {
          url: objectUrl,
          cleanup: () => URL.revokeObjectURL(objectUrl),
          blobSize: blob.size,
          blobType: blob.type,
          fetchedAt: new Date().toISOString()
        };
      } catch (error) {
        console.error('Error fetching authenticated blob:', error);
        throw error;
      }
    },
    enabled: Boolean(enabled && !!fetchUrl),
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    ...customOptions,
    
    // Retry configuration
    retry: (failureCount, error) => {
      // Don't retry 404s or 403s (blob doesn't exist or no permission)
      if (error?.message?.includes('404') || error?.message?.includes('403')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

    // Return object with canonical API properties
  return {
    ...query,
    url: query.data?.url || null,
  };
};

// Export the query client hook for direct access when needed
export { useQueryClient } from '@tanstack/react-query';

// ===========================
// CUSTOM FIELDS HOOKS
// ===========================

// Get all custom fields for a project/tenant
export const useCustomFields = (projectId = null, includeOptions = false) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useQuery({
    queryKey: ['custom-fields', projectId, includeOptions],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (projectId) params.set('project_id', projectId);
      if (includeOptions) params.set('include_options', 'true');
      
      return apiFetch(
        authenticatedFetch,
        `/custom-fields?${params.toString()}`
      );
    }
  });
};

// Get a specific custom field
export const useCustomField = (fieldId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useQuery({
    queryKey: ['custom-fields', fieldId],
    queryFn: async () => {
      return apiFetch(
        authenticatedFetch,
        `/custom-fields/${fieldId}`
      );
    },
    enabled: Boolean(fieldId)
  });
};

/**
 * Get options for a specific custom field
 * @param {string} fieldId - The custom field ID
 * @returns {object} Query result with options array
 */
export const useCustomFieldOptions = (fieldId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery({
    queryKey: ['custom-field-options', fieldId],
    queryFn: async () => {
      const data = await apiFetch(
        authenticatedFetch,
        `/custom-fields/${fieldId}`
      );
      return data.options || [];
    },
    enabled: Boolean(fieldId)
  });
};

// Create a new custom field
export const useCreateCustomField = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (fieldData) => {
      return apiFetch(authenticatedFetch, '/custom-fields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fieldData)
      });
    },
    onSuccess: () => {
      // Invalidate custom fields queries
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    }
  });
};

// Update a custom field
export const useUpdateCustomField = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ fieldId, fieldData }) => {
      return apiFetch(authenticatedFetch, `/custom-fields/${fieldId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fieldData)
      });
    },
    onSuccess: (data, variables) => {
      // Invalidate specific field and all custom fields
      queryClient.invalidateQueries({ queryKey: ['custom-fields', variables.fieldId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    }
  });
};

/**
 * Update custom field options specifically
 * @param {object} params - Parameters: { fieldId, options }
 * @returns {object} Mutation result
 */
export const useUpdateCustomFieldOptions = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fieldId, options }) => {
      return apiFetch(
        authenticatedFetch,
        `/custom-fields/${fieldId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ options })
        }
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', variables.fieldId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      queryClient.invalidateQueries({ queryKey: ['custom-field-options', variables.fieldId] });
    }
  });
};

/**
 * Reorder custom field options
 * @param {object} params - Parameters: { fieldId, options } where options have id and display_order
 * @returns {object} Mutation result
 */
export const useReorderCustomFieldOptions = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fieldId, options }) => {
      // Options array should have { id, display_order } for each option
      return apiFetch(
        authenticatedFetch,
        `/custom-fields/${fieldId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ options })
        }
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', variables.fieldId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      queryClient.invalidateQueries({ queryKey: ['custom-field-options', variables.fieldId] });
    }
  });
};

// Delete a custom field
export const useDeleteCustomField = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (fieldId) => {
      return apiFetch(authenticatedFetch, `/custom-fields/${fieldId}`, {
        method: 'DELETE'
      });
    },
    onSuccess: (data, fieldId) => {
      // Invalidate specific field and all custom fields
      queryClient.invalidateQueries({ queryKey: ['custom-fields', fieldId] });
      queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      // Also invalidate custom field values
      queryClient.invalidateQueries({ queryKey: ['custom-field-values'] });
      // Invalidate field configs to refresh layouts
      queryClient.invalidateQueries({ queryKey: ['workflow-stages'] });
      queryClient.invalidateQueries({ queryKey: ['stage-field-mappings'] });
      // Invalidate any items that might reference this field
      queryClient.invalidateQueries({ queryKey: ['items'] });
      // Force refresh of all cached data that depends on field configurations
      queryClient.invalidateQueries();
    }
  });
};

// Get custom field values for an item
export const useCustomFieldValues = (itemId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useQuery({
    queryKey: ['custom-field-values', itemId],
    queryFn: async () => {
      return apiFetch(
        authenticatedFetch,
        `/custom-field-values/${itemId}`
      );
    },
    enabled: Boolean(itemId)
  });
};

// Get a specific custom field value
export const useCustomFieldValue = (itemId, fieldId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useQuery({
    queryKey: ['custom-field-values', itemId, fieldId],
    queryFn: async () => {
      return apiFetch(
        authenticatedFetch,
        `/custom-field-values/${itemId}/${fieldId}`
      );
    },
    enabled: Boolean(itemId && fieldId)
  });
};

// Save custom field values for an item
export const useSaveCustomFieldValues = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemId, values }) => {
      return apiFetch(authenticatedFetch, '/custom-field-values', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, values })
      });
    },
    onSuccess: (data, variables) => {
      // Invalidate custom field values for this item
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId] 
      });
      // Also invalidate the item details to reflect custom field changes
      queryClient.invalidateQueries({ 
        queryKey: ['items', variables.itemId] 
      });

            // Stale Items report depends on item activity
            queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });
    }
  });
};

// Update a specific custom field value
export const useUpdateCustomFieldValue = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
        mutationFn: async ({ itemId, fieldId, value, metadata, costAmount }) => {
            const payload = { value };
            const itemCache = queryClient.getQueryData(queryKeys.item(itemId));

            if (metadata && Object.keys(metadata).length > 0) {
                payload.metadata = metadata;
            }

            if (costAmount !== undefined && costAmount !== null) {
                payload.cost_amount = costAmount;
            }

            payload.expected_updated_at = itemCache?.updated_at || null;
            payload.expected_row_version = itemCache?.row_version || null;

      return apiFetch(
        authenticatedFetch, 
        `/custom-field-values/${itemId}/${fieldId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
        }
      );
    },
        onMutate: (variables) => {
            if (options.onMutate) {
                return options.onMutate(variables);
            }
            return undefined;
        },
        onSuccess: async (data, variables, context) => {
      // Add a small delay to ensure backend processing (like risk calculation) completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Invalidate custom field values for this item and specific field
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId, variables.fieldId] 
      });
      // Also invalidate the item details
      queryClient.invalidateQueries({ 
        queryKey: ['items', variables.itemId] 
      });

            // Stale Items report depends on item activity
            queryClient.invalidateQueries({ queryKey: ['reports', 'stale-items'] });

            if (options.onSuccess) {
                await options.onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            if (options.onError) {
                options.onError(error, variables, context);
            }
        },
        onSettled: (data, error, variables, context) => {
            if (options.onSettled) {
                options.onSettled(data, error, variables, context);
            }
    }
  });
};

// Delete a custom field value
export const useDeleteCustomFieldValue = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemId, fieldId }) => {
      return apiFetch(
        authenticatedFetch,
        `/custom-field-values/${itemId}/${fieldId}`,
        {
          method: 'DELETE'
        }
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate custom field values for this item and specific field
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId, variables.fieldId] 
      });
      // Also invalidate the item details
      queryClient.invalidateQueries({ 
        queryKey: ['items', variables.itemId] 
      });
    }
  });
};

// Delete all custom field values for an item
export const useDeleteAllCustomFieldValues = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ itemId, force = false }) => {
      return apiFetch(
        authenticatedFetch,
        `/custom-field-values/${itemId}?force=${force}`,
        {
          method: 'DELETE'
        }
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate all custom field values for this item
      queryClient.invalidateQueries({ 
        queryKey: ['custom-field-values', variables.itemId] 
      });
      // Also invalidate the item details
      queryClient.invalidateQueries({ 
        queryKey: ['items', variables.itemId] 
      });
    }
  });
};

// Trashcan hooks for soft delete management

/**
 * Get all trashcan items (soft deleted items from all tables)
 */
export function useTrashcanItems(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredQuery(
        queryKeys.trashcan,
        async () => {
            return apiFetch(authenticatedFetch, '/trashcan');
        },
        {
            staleTime: 0, // Always refetch
            cacheTime: 0, // Don't cache
            refetchOnMount: 'always', // Always refetch when component mounts
            refetchOnWindowFocus: true, // Refetch when window gets focus
            ...options
        }
    );
}

/**
 * Restore a soft deleted item
 */
export function useRestoreTrashcanItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ tableName, id }) => {
            return apiFetch(authenticatedFetch, `/trashcan/${tableName}/${id}/restore`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
        },
        {
            onSuccess: (data, variables) => {
                // Invalidate trashcan to refresh the list
                queryClient.invalidateQueries({ queryKey: queryKeys.trashcan });
                
                // Invalidate the relevant table queries to show restored item
                const tableQueryMap = {
                    'items': queryKeys.items,
                    'tasks': queryKeys.tasks,
                    'brainstorming': queryKeys.brainstorming
                };
                
                if (tableQueryMap[variables.tableName]) {
                    queryClient.invalidateQueries({ queryKey: tableQueryMap[variables.tableName] });
                }
            },
            ...options
        }
    );
}

/**
 * Permanently delete (hard delete) a trashcan item
 */
export function useHardDeleteTrashcanItem(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async ({ tableName, id }) => {
            return apiFetch(authenticatedFetch, `/trashcan/${tableName}/${id}`, {
                method: 'DELETE'
            });
        },
        {
            onSuccess: () => {
                // Invalidate trashcan to refresh the list
                queryClient.invalidateQueries({ queryKey: queryKeys.trashcan });
            },
            ...options
        }
    );
}

/**
 * Permanently delete (hard delete) many trashcan items in a single request
 */
export function useBulkHardDeleteTrashcanItems(options = {}) {
    const queryClient = useQueryClient();
    const authenticatedFetch = useAuthenticatedFetch();

    return useConfiguredMutation(
        async ({ items }) => {
            return apiFetch(authenticatedFetch, `/trashcan/bulk-hard-delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: queryKeys.trashcan });
            },
            ...options
        }
    );
}

// Feedback submission hook
export function useSubmitFeedback(options = {}) {
    const authenticatedFetch = useAuthenticatedFetch();
    
    return useConfiguredMutation(
        async (payload) => {
            // Support both JSON and multipart/form-data (FormData)
            if (payload instanceof FormData) {
                return apiFetch(authenticatedFetch, '/feedback', {
                    method: 'POST',
                    body: payload
                });
            }

            return apiFetch(authenticatedFetch, '/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
        },
        {
            ...options
        }
    );
}
